import React, { useState } from 'react';
import { formatCount } from '../../utils/numberFormatter';
import { arabicPlural } from '../../utils/alertDisplay';
import { TEXT, errorText } from '../../config/guestReviewLabels';

/**
 * The parts both Reviews-page lists share (tasks/post-checkout-review-and-replies.md Phases 2–3) — one
 * copy, so the two cards cannot drift apart in how a status, a choice, a refusal or a deadline reads.
 */

export const StatusChip = ({ status, labels }) => (
  <span className={`gr-chip gr-chip--${status}`}>{labels[status] || status}</span>
);

// Neutral until picked, brand purple once picked — the same for every option (no colour judgement).
export const ChoiceButton = ({ active, onClick, disabled, children }) => (
  <button
    type="button"
    className={`gr-choice touch-btn touch-btn-sm ${active ? 'gr-choice--active' : ''}`}
    aria-pressed={active}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

export const ErrorLine = ({ message }) => (message ? <div className="gr-error" role="alert">{message}</div> : null);

/** Days left, from the server's count-down: below 0 the deadline has passed (the sweep closes it). */
export function daysLeftLabel(days) {
  if (days === null || days === undefined) return null;
  if (days < 0) return TEXT.deadlinePassed;
  if (days === 0) return TEXT.endsToday;
  return `${TEXT.daysLeft} ${formatCount(days)} ${arabicPlural(days, TEXT.day, TEXT.twoDays, TEXT.days)}`;
}

/**
 * Runs one mutation and turns a refusal into words (from its code) instead of throwing. `toText` is the
 * page's own code → words map — the Reviews page's by default; the Complaints page passes its own.
 */
export function useRunner(toText = errorText) {
  const [error, setError] = useState(null);
  const run = async (mutation, vars, onDone) => {
    setError(null);
    try {
      // The answer is handed on: most actions only answer `{ id }`, but a repair task says whether a
      // supplies request JOINED the unit's open refill task and which one. Callers may ignore it.
      const result = await mutation.mutateAsync(vars);
      if (onDone) onDone(result);
    } catch (err) {
      setError(toText(err));
    }
  };
  return { error, run };
}

/**
 * The direction of a text someone types: the page's right-to-left while empty, then the text's own —
 * an English or French reply must not be laid out as Arabic (the inbox composer's rule).
 */
export const textDir = (value) => (String(value || '').trim() ? 'auto' : 'rtl');
